import { useEffect, useRef } from 'react';

export type ParticleType =
  | 'fire'
  | 'smoke'
  | 'confetti'
  | 'gold'
  | 'spark'
  | 'rain'
  | 'burst';

interface ParticleConfig {
  n: number;
  col: string[];
  sp: number;
  g: number;
  sz: [number, number];
  life: number;
  top?: boolean;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  sz: number;
  col: string;
  life: number;
  max: number;
  rot: number;
  vr: number;
  shape: 'rect' | 'circle';
}

const C: Record<ParticleType, ParticleConfig> = {
  fire: { n: 70, col: ['#ff4500', '#ff6b00', '#ffaa00', '#ffd700', '#ff2200'], sp: 9, g: -0.16, sz: [3, 10], life: 75 },
  smoke: { n: 45, col: ['#888', '#aaa', '#666'], sp: 4, g: -0.08, sz: [8, 22], life: 95 },
  confetti: { n: 110, col: ['#3b82f6', '#60a5fa', '#fbbf24', '#22c55e', '#ef4444', '#a78bfa', '#fff'], sp: 11, g: 0.25, sz: [4, 9], life: 110 },
  gold: { n: 60, col: ['#fbbf24', '#fcd34d', '#f59e0b', '#fff8dc'], sp: 10, g: 0.15, sz: [3, 8], life: 90 },
  spark: { n: 35, col: ['#fff', '#fbbf24', '#60a5fa'], sp: 13, g: 0.1, sz: [2, 5], life: 55 },
  rain: { n: 130, col: ['#3b82f6', '#60a5fa', '#fbbf24', '#22c55e', '#ef4444', '#a78bfa', '#fff'], sp: 3, g: 0.3, sz: [4, 10], life: 170, top: true },
  burst: { n: 50, col: ['#fff', '#60a5fa', '#3b82f6'], sp: 14, g: 0.05, sz: [2, 6], life: 45 },
};

const pick = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)];

interface ParticleCanvasProps {
  type: ParticleType;
  x?: number;
  y?: number;
  onDone?: () => void;
  colors?: string[];   // overrides the preset palette (skin-themed)
}

export function ParticleCanvas({ type, x = 0.5, y = 0.5, onDone, colors }: ParticleCanvasProps) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const W = (cv.width = cv.clientWidth || cv.offsetWidth);
    const H = (cv.height = cv.clientHeight || cv.offsetHeight);
    const ox = W * x;
    const oy = H * y;

    const cfg = C[type] ?? C.confetti;
    const parts: Particle[] = Array.from({ length: cfg.n }, () => {
      const a = Math.random() * Math.PI * 2;
      const v = cfg.sp * (0.4 + Math.random() * 0.6);
      const launch = type === 'confetti' || type === 'gold' ? 4 : 0;
      return {
        x: cfg.top ? Math.random() * W : ox,
        y: cfg.top ? -20 - Math.random() * 100 : oy,
        vx: cfg.top ? (Math.random() - 0.5) * 2 : Math.cos(a) * v,
        vy: cfg.top ? cfg.sp * (0.5 + Math.random()) : Math.sin(a) * v - launch,
        sz: cfg.sz[0] + Math.random() * (cfg.sz[1] - cfg.sz[0]),
        col: pick(colors && colors.length ? colors : cfg.col),
        life: cfg.life * (0.6 + Math.random() * 0.4),
        max: cfg.life,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        shape: type === 'confetti' || type === 'rain' ? (Math.random() > 0.5 ? 'rect' : 'circle') : 'circle',
      };
    });

    let raf = 0;
    let f = 0;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      ctx.clearRect(0, 0, W, H);
      onDoneRef.current?.();
    };

    const run = () => {
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      for (const p of parts) {
        if (p.life <= 0) continue;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += cfg.g;
        p.vx *= 0.98;
        p.life -= 1;
        p.rot += p.vr;
        const al = Math.max(0, p.life / p.max);
        ctx.globalAlpha = al;
        ctx.fillStyle = p.col;
        if (type === 'fire' || type === 'smoke') {
          const r = p.sz * (type === 'smoke' ? 1 + (1 - al) : al);
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'rect') {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillRect(-p.sz / 2, -p.sz / 2, p.sz, p.sz * 1.6);
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.sz, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      f += 1;
      if (alive && f < 280) {
        raf = requestAnimationFrame(run);
      } else {
        finish();
      }
    };

    run();

    return () => {
      cancelAnimationFrame(raf);
    };
    // colors is read once at particle-spawn time (like onDone via ref); adding it
    // to deps would restart this one-shot animation when callers pass inline arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, x, y]);

  return (
    <canvas
      ref={ref}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}
